import { useState, useEffect } from "react";
import * as authSlice from '../features/auth/auth.slice';
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Alert, Avatar, Box, Button, Container, TextField, Typography } from "@mui/material";
import Copyright from "../components/copyright";
import { useNavigate } from "react-router-dom";
import { PasswordChangeRequest, StatusResponseWithMessage } from "../service/model/auth.model";
import { LockOutlinedIcon } from "../shared/icons";

const ChangePassword = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const passwordChangeResponse = useAppSelector(authSlice.selectPasswordChangeResponse);

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (passwordChangeResponse && passwordChangeResponse.status === 'success') {
            dispatch(authSlice.resetState());
            navigate('/login');
        }
    }, [passwordChangeResponse]);

    const changePassword = async () => {
        const validationResult = isValid();
        if(validationResult.status === 'failed') {
            setError(validationResult.message);
            return;
        }
        const requestPasswordChange = async () => {
            const request: PasswordChangeRequest = { oldPassword: oldPassword ?? '', newPassword: newPassword }
            await dispatch(authSlice.changePassword(request));
        }

        await requestPasswordChange();
    }

    function handleOldPasswordChange(value: string): void {
        setOldPassword(value);
    }

    function handleNewPasswordChange(value: string): void {
        setNewPassword(value);
    }

    function handleConfirmPasswordChange(value: string): void {
        setConfirmPassword(value);
    }

    useEffect(() => {
        setError(undefined);
    }, [oldPassword, newPassword, confirmPassword]);

    function isValid(): StatusResponseWithMessage {
        if(oldPassword.trim() === '' || newPassword.trim() === '' || confirmPassword.trim() === '') {
            return { status: 'failed', message: 'All fields are required' };
        }
        if(newPassword !== confirmPassword) {
            return { status: 'failed', message: 'New password and confirm password do not match' };
        }
        if(newPassword.length < 8) {
            return { status: 'failed', message: 'New password must be at least 8 characters long' };
        }
        // if(!PASSWORD_PATTERN.test(newPassword)) {
        //     return { status: 'failed', message: 'New password must contain at least one uppercase letter, one lowercase letter, one digit and one special character' };
        // }
       return { status: 'success', message: 'Validation successful' };
    }

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5" sx={{ m: 3 }}>
                    Change your password
                </Typography>
                <Box sx={{ mt: 1 }}>
                    {error && <Alert severity="error" variant="filled">{error}</Alert>}
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="oldPassword"
                        label="Old Password"
                        type="password"
                        id="oldPassword"
                        onChange={e => handleOldPasswordChange(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="newPassword"
                        label="New Password"
                        type="password"
                        id="newPassword"
                        onChange={e => handleNewPasswordChange(e.target.value)}
                    />                    
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        id="confirmPassword"
                        onChange={e => handleConfirmPasswordChange(e.target.value)}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        onClick={changePassword}
                        // disabled={error !== undefined}
                    >
                        Change Password
                    </Button>
                </Box>
            </Box>
            <Box sx={{ mt: 8, mb: 4 }}>
                <Copyright />
            </Box>
        </Container>
    );
}

export default ChangePassword;